/**
 * Black 1/1 Registry — presentation helpers.
 *
 * Msg 245 · Redesign the right-side information area of the public
 * Black 1/1 detail page as a premium provenance registry. These
 * helpers are pure so we can Jest-test them without touching React.
 */

const _norm = (u) => {
  try { return new URL(String(u).trim()); } catch (_e) { return null; }
};

const _host = (u) => {
  const p = _norm(u); if (!p) return "";
  return p.hostname.replace(/^www\./i, "").toLowerCase();
};

/**
 * Domain-aware label + CTA text for the KNOWN COLLECTOR social link.
 * Returns `{ label, cta }` or `null` when the input is not a valid URL.
 * `label` is a short one-word service name for the section chrome
 * ("Instagram", "TikTok"…). `cta` is the CTA button text
 * ("View Instagram ↗"). Unknown hosts collapse to `View profile ↗`.
 */
export function socialCtaFor(url) {
  const h = _host(url);
  if (!h) return null;
  if (h === "instagram.com" || h.endsWith(".instagram.com"))
    return { host: h, label: "Instagram", cta: "View Instagram" };
  if (h === "tiktok.com" || h.endsWith(".tiktok.com"))
    return { host: h, label: "TikTok",    cta: "View TikTok" };
  if (h === "x.com" || h === "twitter.com" || h.endsWith(".x.com") || h.endsWith(".twitter.com"))
    return { host: h, label: "X",         cta: "View X" };
  if (h === "facebook.com" || h.endsWith(".facebook.com") || h === "fb.com")
    return { host: h, label: "Facebook",  cta: "View Facebook" };
  if (h === "youtube.com" || h.endsWith(".youtube.com") || h === "youtu.be")
    return { host: h, label: "YouTube",   cta: "View YouTube" };
  if (h === "ebay.com" || h.endsWith(".ebay.com"))
    return { host: h, label: "eBay",      cta: "View eBay" };
  return { host: h, label: "profile", cta: "View profile" };
}

/**
 * Domain-aware "DOCUMENTED BY" label for the evidence source. Falls
 * back to a readable domain (`example.co.uk`) when the source is not
 * a known site. Returns `null` for invalid URLs so the row can be
 * hidden.
 */
export function evidenceLabelFor(url) {
  const h = _host(url);
  if (!h) return null;
  const known = {
    "soccerstickersfc.net": "Soccer Stickers FC",
    "instagram.com":        "Instagram",
    "reddit.com":           "Reddit",
    "tiktok.com":           "TikTok",
    "ebay.com":             "eBay",
    "youtube.com":          "YouTube",
    "youtu.be":             "YouTube",
    "x.com":                "X",
    "twitter.com":          "X",
    "facebook.com":         "Facebook",
  };
  if (known[h]) return known[h];
  for (const key of Object.keys(known)) {
    if (h.endsWith("." + key)) return known[key];
  }
  return h; // safe fallback — always a lowercased, www-stripped domain
}

/**
 * Format an ISO-8601 timestamp as a human-readable calendar date in
 * UTC (`February 15, 2026`). Returns `""` for any invalid input so
 * the caller can hide the row.
 */
export function formatSurfacedDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric", timeZone: "UTC",
  });
}
